import { createSlice } from '@reduxjs/toolkit';
import { MessageEntity, ThreadEntity } from '@interpals/api-web';

import { MESSAGES_LIMIT, THREADS_LIMIT } from 'constants/limits';

import {
    clearCurrentThread,
    clearFailedMessageFromMainThread,
    clearReplyMessage,
    clearThreadDraft,
    clearThreadTyping,
    clearMessages,
    clearThreads,
    deleteMessage,
    deleteThread,
    getMessages,
    getThreadByUsername,
    getThreads,
    markThreadViewed,
    modGetThreadByOwnerContactUids,
    pushTempMessageToMainThread,
    sendMessage,
    sendPhoto,
    setCurrentThread,
    setLastThreadMessage,
    setLastViewedMessage,
    setMessagesToSend,
    setReplyMessage,
    setThreadDraft,
    setThreadTyping,
    WS_NEW_MESSAGE,
    restoreMessages,
} from './actions';
import { MessagesList, MessagesListItem } from './types';

export interface ChatState {
    messages: {
        list: MessagesList;
        status: {
            queued: string[];
            failed: string[];
        };
        messageToSend: string;
        reply: MessagesListItem | null;
        hasMore: boolean;
    };
    threads: {
        list: ThreadEntity[];
        current: ThreadEntity | null;
        threadTypingIds: string[];
        hasMore: boolean;
    };
}

const initialState: ChatState = {
    messages: {
        list: [],
        status: {
            queued: [],
            failed: [],
        },
        messageToSend: '',
        reply: null,
        hasMore: true,
    },
    threads: {
        list: [],
        current: null,
        threadTypingIds: [],
        hasMore: true,
    },
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getMessages.fulfilled, (state, { payload }) => {
            if (payload.length > 0) {
                if (state.messages.list.some((message) => message.id === payload[0].id)) return state;

                state.messages.list = [...state.messages.list, ...payload];
            }

            state.messages.hasMore = payload.length === MESSAGES_LIMIT;
        });

        builder.addCase(pushTempMessageToMainThread, (state, { payload }) => {
            // As messages presented as reversed array we need to push new messages at beginning
            state.messages.list.unshift(payload);

            state.messages.status.queued.push(payload.id);
        });

        builder.addCase(clearFailedMessageFromMainThread, (state, { payload }) => {
            // Delete failed message from main thread
            state.messages.list = state.messages.list.filter((message) => message.id !== payload);
            // Delete failed message id from status.failed array
            state.messages.status.failed = state.messages.status.failed.filter((messageId) => messageId !== payload);
        });

        builder.addCase(sendMessage.fulfilled, (state, { payload }) => {
            // tmp_id presented by MessageEntity
            const tempId = (payload as MessageEntity).tmp_id;
            // Find temp message id in main thread
            const tempMessageId = state.messages.list.findIndex((message) => message.id === tempId);

            if (tempId) {
                state.messages.status.queued = state.messages.status.queued.filter((messageId) => messageId !== tempId);
                state.messages.status.failed = state.messages.status.queued.filter((messageId) => messageId !== tempId);
            }

            // Update temp message id to real id when action is fulfilled
            state.messages.list[tempMessageId].id = payload.id;

            // Update the thread info with that fetched from backend (won't exist if this is a temp message)
            if ('thread' in payload) {
                state.threads.list = state.threads.list.map((thread) =>
                    thread.id === payload.thread_id && payload.thread ? payload.thread : thread,
                );
            }

            state.threads.list = state.threads.list.sort((a: ThreadEntity, b: ThreadEntity) =>
                (b.last_msg_id?.toString() ?? '').localeCompare(a.last_msg_id?.toString() ?? ''),
            );
        });

        builder.addCase(sendMessage.rejected, (state, { payload }) => {
            if (payload) {
                // Push message id to status.failed array
                state.messages.status.failed.push(payload.id);
                // Delete message id from status.queued array
                state.messages.status.queued = state.messages.status.queued.filter(
                    (messageId) => messageId !== payload.id,
                );
            }
        });

        builder.addCase(sendPhoto.rejected, (state, { payload }) => {
            if (payload) {
                // Similar to sendMessage logic
                state.messages.status.failed.push(payload.id);
                state.messages.status.queued = state.messages.status.queued.filter(
                    (messageId) => messageId !== payload.id,
                );
            }
        });

        builder.addCase(deleteMessage.fulfilled, (state, { payload }) => {
            // Delete on BE, we need only to remove message from main thread & view
            state.messages.list = state.messages.list.filter((message) => message.id !== payload);
        });

        builder.addCase(setMessagesToSend, (state, { payload }) => {
            state.messages.messageToSend = payload;
        });

        builder.addCase(setReplyMessage, (state, { payload }) => {
            state.messages.reply = payload;
        });

        builder.addCase(clearReplyMessage, (state) => {
            state.messages.reply = null;
        });

        builder.addCase(clearMessages, (state) => {
            return {
                ...state,
                messages: initialState.messages,
            };
        });

        builder.addCase(getThreads.fulfilled, (state, { payload }) => {
            state.threads.list = [...state.threads.list, ...payload.data];
            state.threads.hasMore = payload.data.length === THREADS_LIMIT;
        });

        builder.addCase(clearThreads, (state) => {
            return {
                ...state,
                threads: initialState.threads,
            };
        });

        builder.addCase(deleteThread.fulfilled, (state, { payload }) => {
            state.threads.list = state.threads.list.filter((thread) => thread.id !== payload);
        });

        builder.addCase(setCurrentThread, (state, { payload }) => {
            state.threads.current = payload;
        });

        builder.addCase(clearCurrentThread, (state) => {
            state.threads.current = null;
        });

        builder.addCase(markThreadViewed.fulfilled, (state, { payload }) => {
            state.threads.list = state.threads.list.map((thread) =>
                thread.id === payload
                    ? {
                          ...thread,
                          new: 0,
                      }
                    : thread,
            );
        });

        builder.addCase(setThreadTyping, (state, { payload }) => {
            if (!state.threads.threadTypingIds.some((id) => id === payload)) {
                state.threads.threadTypingIds = [...state.threads.threadTypingIds, payload];
            }
        });

        builder.addCase(getThreadByUsername.fulfilled, (state, { payload }) => {
            if (payload.data.length !== 0) {
                state.threads.list = [...state.threads.list, ...payload.data];
                state.threads.hasMore = payload.data.length === THREADS_LIMIT;
            }
        });

        builder.addCase(modGetThreadByOwnerContactUids.fulfilled, (state, { payload }) => {
            if (payload) {
                state.threads.current = payload;
            }
        });

        builder.addCase(clearThreadTyping, (state, { payload }) => {
            if (state.threads.threadTypingIds.some((id) => id === payload)) {
                state.threads.threadTypingIds = state.threads.threadTypingIds.filter((id) => id !== payload);
            }
        });

        builder.addCase(setLastThreadMessage, (state, { payload }) => {
            state.threads.list = state.threads.list.map((thread) =>
                thread.id === payload.thread_id
                    ? { ...thread, msg_snippet: payload.message, last_msg_sender_id: payload.sender_id }
                    : thread,
            );
        });

        builder.addCase(WS_NEW_MESSAGE, (state, { payload }) => {
            if (state.messages.list[0]?.thread_id === payload.thread_id) {
                state.messages.list = [payload, ...state.messages.list];
            }

            state.threads.list = state.threads.list
                .map((thread) =>
                    thread.id === payload.thread_id
                        ? {
                              ...thread,
                              last_msg_id: payload.id,
                              msg_snippet: payload.message,
                              last_msg_datetime: payload.created,
                              new: thread.new + 1,
                          }
                        : thread,
                )
                .sort((a: ThreadEntity, b: ThreadEntity) =>
                    (b.last_msg_id?.toString() ?? '').localeCompare(a.last_msg_id?.toString() ?? ''),
                );

            if (state.threads.threadTypingIds.some((id) => id === payload.sender_id)) {
                state.threads.threadTypingIds = state.threads.threadTypingIds.filter((id) => id !== payload.sender_id);
            }
        });

        builder.addCase(setLastViewedMessage, (state, { payload: { data: viewedData } }) => {
            if (viewedData && state.threads.current && state.threads.current.uid !== viewedData.viewer_uid) {
                const currentThreadId = state.threads.current.id;

                const updatedThreadData = {
                    target_last_opened: viewedData.target_last_opened,
                    target_last_read_msg_id: viewedData.target_last_read_msg_id,
                    last_opened: viewedData.last_opened,
                    last_msg_id: viewedData.last_message_id,
                };

                if (viewedData.thread_id === state.threads.current.id) {
                    state.threads.current = { ...state.threads.current, ...updatedThreadData };
                }
                state.threads.list = state.threads.list.map((thread) =>
                    thread.id === currentThreadId ? { ...thread, ...updatedThreadData } : thread,
                );
            }
        });

        builder.addCase(setThreadDraft.fulfilled, (state, { payload: draft }) => {
            const targetThread = state.threads.list.find((thread) => thread.id === draft.thread_id);

            if (targetThread) {
                targetThread.draft = draft;
            }

            if (state.threads.current && state.threads.current.id === draft.thread_id) {
                state.threads.current.draft = draft;
            }
        });

        builder.addCase(clearThreadDraft, (state) => {
            const threadId = state?.threads.current?.id;
            if (threadId && state.threads.current) {
                state.threads.current.draft = undefined;

                const targetThread = state.threads.list.find((thread) => thread.id === threadId);
                if (targetThread) {
                    targetThread.draft = undefined;
                }
            }
        });

        builder.addCase(restoreMessages.fulfilled, (state, { payload }) => {
            const { thread, messages } = payload;

            const isCurrentThreadRestored = state.threads.current?.id === thread.id;
            const idx = state.threads.list.findIndex((t) => t.id === thread.id);

            if (isCurrentThreadRestored) {
                state.threads.current = thread;
            }

            state.threads.list[idx] = thread;
            state.messages.list = messages;
        });
    },
});

export default chatSlice.reducer;
